import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { AdminPageHeader } from '@/components/admin/admin-page-header';
import { FilterBar } from '@/components/admin/filter-bar';
import { JobsTable } from '@/components/admin/jobs/jobs-table';
import { RetryFailedJobsButton } from '@/components/admin/jobs/retry-failed-jobs-button';
import { JobTypePicker } from '@/components/admin/jobs/job-type-picker';
import { OrganizationPicker } from '@/components/admin/shared/organization-picker';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAdminJobs } from '@/hooks/data/admin/use-jobs';
import {
  BackgroundJobStatus,
  BackgroundJobType,
} from '@/types/generated/enums';
import type {
  BackgroundJobStatus as BackgroundJobStatusType,
  BackgroundJobType as BackgroundJobTypeT,
} from '@/types/generated';

const searchSchema = z.object({
  organization_id: z.number().int().positive().optional(),
  status: z.nativeEnum(BackgroundJobStatus).optional(),
  job_type: z.nativeEnum(BackgroundJobType).optional(),
  created_after: z.string().date().optional(),
  created_before: z.string().date().optional(),
  offset: z.number().int().nonnegative().default(0),
});

const LIMIT = 20;

export const Route = createFileRoute('/_authenticated/admin/jobs/')({
  validateSearch: searchSchema,
  staticData: { title: 'Background jobs' },
  component: AdminJobsPage,
});

function AdminJobsPage(): React.ReactElement {
  const {
    organization_id,
    status,
    job_type,
    created_after,
    created_before,
    offset,
  } = Route.useSearch();
  const navigate = Route.useNavigate();

  const jobsQuery = useAdminJobs({
    organization_id,
    status,
    job_type,
    created_after,
    created_before,
    limit: LIMIT,
    offset,
  });

  const setOrgId = (value: number | undefined): void => {
    void navigate({
      search: (s) => ({ ...s, organization_id: value, offset: 0 }),
    });
  };

  const setStatus = (value: string): void => {
    void navigate({
      search: (s) => ({
        ...s,
        status:
          value === 'ALL' ? undefined : (value as BackgroundJobStatusType),
        offset: 0,
      }),
    });
  };

  const setJobType = (value: BackgroundJobTypeT | undefined): void => {
    void navigate({
      search: (s) => ({ ...s, job_type: value, offset: 0 }),
    });
  };

  const setCreatedAfter = (value: string): void => {
    void navigate({
      search: (s) => ({
        ...s,
        created_after: value || undefined,
        offset: 0,
      }),
    });
  };

  const setCreatedBefore = (value: string): void => {
    void navigate({
      search: (s) => ({
        ...s,
        created_before: value || undefined,
        offset: 0,
      }),
    });
  };

  return (
    <main>
      <AdminPageHeader
        title="Background jobs"
        description="Monitor and retry background jobs across all organizations"
      />
      <FilterBar>
        <OrganizationPicker value={organization_id} onChange={setOrgId} />
        <Select value={status ?? 'ALL'} onValueChange={setStatus}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All statuses</SelectItem>
            <SelectItem value={BackgroundJobStatus.PENDING}>Pending</SelectItem>
            <SelectItem value={BackgroundJobStatus.IN_PROGRESS}>
              In progress
            </SelectItem>
            <SelectItem value={BackgroundJobStatus.COMPLETED}>
              Completed
            </SelectItem>
            <SelectItem value={BackgroundJobStatus.FAILED}>Failed</SelectItem>
          </SelectContent>
        </Select>
        <JobTypePicker value={job_type} onChange={setJobType} />
        <DatePicker
          ariaLabel="Created after"
          value={created_after ?? undefined}
          onChange={setCreatedAfter}
          max={created_before ?? undefined}
          className="w-40"
        />
        <DatePicker
          ariaLabel="Created before"
          value={created_before ?? undefined}
          onChange={setCreatedBefore}
          min={created_after ?? undefined}
          className="w-40"
        />
        <RetryFailedJobsButton
          status={status}
          jobType={job_type}
          createdAfter={created_after}
          createdBefore={created_before}
          organizationId={organization_id}
        />
      </FilterBar>
      <JobsTable
        query={jobsQuery}
        offset={offset}
        limit={LIMIT}
        onPageChange={(newOffset) =>
          void navigate({ search: (s) => ({ ...s, offset: newOffset }) })
        }
      />
    </main>
  );
}
